import { Link } from "react-router-dom";
import { Heart, Gift, Repeat, Building2, ArrowUpRight, ArrowRight } from "lucide-react";
import { PageHeader, SectionLabel } from "@/components/shared";
import { FadeUp, Reveal } from "@/components/motion/Reveal";
import { ASSETS, SITE } from "@/lib/assets";

// External giving link
const GIVE_URL = "https://donate.stripe.com/fZebMa78KarQ6ROdQQ";

const WAYS = [
  { icon: Gift, title: "One-Time Gift", body: "Make a single donation and fuel the mission today — every dollar carries the Gospel further." },
  { icon: Repeat, title: "Monthly Partner", body: "Become a sustaining partner. Consistent support lets us plan boldly and reach farther." },
  { icon: Building2, title: "Church Partnership", body: "Churches and organizations can partner with us to host awakening events and training." },
];

const IMPACT = [
  { k: "Awaken", v: "Fund gatherings that call the Church back to fire." },
  { k: "Disciple", v: "Resource training that forms multiplying disciples." },
  { k: "Send", v: "Put boots on the ground for street evangelism across Canada." },
];

export default function Give() {
  return (
    <div data-testid="give-page">
      <PageHeader
        testId="give-header"
        label="Partner With Us"
        title={<>Give To The <span className="text-wutn">Mission</span></>}
        subtitle="Your generosity awakens the Church, disciples believers, and carries the Gospel across the nation. Thank you for standing with us."
        image={ASSETS.groupAdults}
      />

      <section className="bg-black py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          {/* Main give CTA */}
          <Reveal>
            <div className="relative overflow-hidden border border-white/10 bg-[#0A0A0A]">
              <div className="absolute inset-0 spotlight" />
              <div className="relative p-8 md:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
                <div className="max-w-2xl">
                  <div className="flex items-center gap-2 text-wutn"><Heart className="h-5 w-5" /><span className="font-cond uppercase tracking-widest text-sm">Every Gift Matters</span></div>
                  <h2 className="mt-4 font-display uppercase text-4xl md:text-6xl leading-[0.9]">Sow Into <span className="text-wutn">Awakening</span></h2>
                  <p className="mt-5 text-zinc-400 text-lg">We keep giving simple and secure. Click below to give safely through our giving platform.</p>
                </div>
                <a href={GIVE_URL} target="_blank" rel="noreferrer" data-testid="give-now-btn" className="shrink-0 inline-flex items-center gap-2 bg-wutn hover:bg-wutn-dark text-white px-10 py-5 font-display uppercase text-2xl transition-colors">
                  Give Now <ArrowUpRight className="h-6 w-6" />
                </a>
              </div>
            </div>
          </Reveal>

          {/* Ways to give */}
          <div className="mt-16">
            <SectionLabel className="mb-10">Ways To Give</SectionLabel>
            <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
              {WAYS.map((w, i) => (
                <FadeUp key={w.title} delay={i * 0.06}>
                  <div className="bg-black p-8 md:p-10 h-full">
                    <span className="inline-flex h-14 w-14 items-center justify-center bg-wutn/10 text-wutn"><w.icon className="h-7 w-7" /></span>
                    <h3 className="mt-6 font-display uppercase text-2xl">{w.title}</h3>
                    <p className="mt-3 text-zinc-400">{w.body}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>

          {/* Impact */}
          <div className="mt-16 grid md:grid-cols-3 gap-8">
            {IMPACT.map((s, i) => (
              <FadeUp key={s.k} delay={i * 0.06}>
                <div className="border-l-2 border-wutn pl-6">
                  <h4 className="font-display uppercase text-3xl text-wutn">{s.k}</h4>
                  <p className="mt-2 text-zinc-400">{s.v}</p>
                </div>
              </FadeUp>
            ))}
          </div>

          <p className="mt-16 text-center text-sm text-zinc-500 max-w-2xl mx-auto">
            Wake-Up The Nation Ministries · {SITE.location}. For questions about giving or to request a receipt, {" "}
            <Link to="/contact" className="text-wutn hover:underline">contact us</Link>.
          </p>
        </div>
      </section>
    </div>
  );
}
